import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import {
  Cell,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Row, Table as RTable } from "@tanstack/table-core";

import { NightReportFieldType } from "@modules/monitor/NightReportScreen/components/NightReportField";

import { useDataTableStore } from "./dataTableStore";
import HeaderCell from "./HeaderCell";

export type TableProps<TData> = {
  tableId: string;
  columns: ColumnDef<TData, any>[];
  data: TData[];
  getRowId?: (originalRow: TData) => string;
  onRowClick?: (originalRow: TData) => void;
  enableSorting?: boolean;
  footerText?: string;
};

export interface VtColumnMeta {
  tooltip?: string;
  colSpan?: (row: Row<any>) => number;
  skipCell?: (row: Row<any>) => boolean;
  type: NightReportFieldType;
}

function colSpan<TData>(row: Row<TData>, cell: Cell<TData, unknown>) {
  const meta = cell.column.columnDef.meta as VtColumnMeta;
  return meta?.colSpan ? meta.colSpan(row) : 1;
}

function skipCell<TData>(row: Row<TData>, cell: Cell<TData, unknown>) {
  const meta = cell.column.columnDef.meta as VtColumnMeta;
  return meta?.skipCell ? meta.skipCell(row) : false;
}

function isLastColumn<TData>(row: Row<TData>, cell: Cell<TData, unknown>) {
  const visibleCells = row.getVisibleCells().filter((c) => !skipCell(row, c));
  const totalColSpanUntilCell = visibleCells
    .slice(0, visibleCells.findIndex((c) => c.id === cell.id) + 1)
    .reduce((sum, c) => sum + colSpan(row, c), 0);
  return totalColSpanUntilCell === row.getVisibleCells().length;
}

function isLastRow<TData>(row: Row<TData>, table: RTable<TData>) {
  return row.index === table.getRowModel().rows.length - 1;
}

export function DataTable<TData>({
  tableId,
  columns,
  data,
  getRowId,
  onRowClick,
  enableSorting = true,
  footerText,
}: TableProps<TData>) {
  const sorting = useDataTableStore((s) => s.sortingByTable[tableId]);
  const setSorting = useDataTableStore((s) => s.setSorting);

  const table = useReactTable<TData>({
    data,
    columns,
    state: { sorting },
    onSortingChange: (updater) => setSorting(tableId, updater),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId,
    enableSorting,
  });

  return (
    <TableContainer
      component={Paper}
      sx={{
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
        boxShadow: "none",
      }}
    >
      <Table stickyHeader>
        <TableHead>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id}>
              {hg.headers.map((header) => (
                <HeaderCell key={header.id} header={header} />
              ))}
            </TableRow>
          ))}
        </TableHead>

        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              hover={!!onRowClick}
              onClick={() => onRowClick?.(row.original)}
              sx={{ cursor: onRowClick ? "pointer" : "default", height: 48 }}
            >
              {row
                .getVisibleCells()
                .filter((cell) => !skipCell(row, cell))
                .map((cell) => (
                  <TableCell
                    key={cell.id}
                    colSpan={colSpan(row, cell)}
                    sx={{
                      padding: 1.5,
                      borderRightWidth: isLastColumn(row, cell) ? 0 : 1,
                      borderBottomWidth: isLastRow(row, table) ? 0 : 1,
                      borderRightStyle: "solid",
                      borderColor: "backgrounds.neutral.cold.default",
                      color: "texts.neutral.subtle",
                      height: 48,
                    }}
                  >
                    <Typography variant={"bodySmallRegular"} component="div">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </Typography>
                  </TableCell>
                ))}
            </TableRow>
          ))}
        </TableBody>
        {!!footerText && (
          <TableFooter>
            <TableRow>
              <TableCell
                colSpan={table.getAllLeafColumns().length}
                align="right"
                sx={{
                  paddingRight: 2.5,
                  paddingTop: 0,
                  paddingBottom: 0,
                  height: 40,
                  borderBottom: 0,
                  borderTop: 1,
                  borderTopColor: "borders.neutral.subtle",
                  color: "texts.neutral.subtle",
                }}
              >
                <Typography variant={"bodySmallMedium"}>
                  {footerText}
                </Typography>
              </TableCell>
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
}
